import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import Lightbox from "../components/Lightbox";
import { api } from "../lib/api";
import { withImageVersion } from "../lib/imageVersion";
import { downloadCertificate } from "../lib/certificate";
import { shareAngelCard } from "../lib/socialCard";
import { downloadFramedPrint } from "../lib/framedPrint";
import { formatCooldownDate, isCooldownActive } from "../lib/cooldown";
import { useMember } from "../context/MemberContext";
import { Download, Frame, Maximize2, Loader2, Share2, Sparkles, Star, X, ShoppingBag, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Platform-honest toast text. On iOS the OS share sheet opens and the user
 * picks a destination directly; on Android we always fall through to the
 * anchor-tag download, which lands the file in the device's Downloads
 * folder — so the toast tells the user exactly where to find it next.
 */
function toastFor(label, result) {
    if (result?.cancelled) return `${label} — cancelled.`;
    if (result?.shared) return `${label} shared.`;
    if (result?.platform === "android-webview") {
        // The relay opened the file in the shell — Downloads landing is the
        // shell's job from here. Tell the user what to look for.
        return `${label} ready. Check your Downloads folder, or look for a share prompt from your browser.`;
    }
    if (result?.platform === "android") {
        return `${label} saved to Downloads — attach it from your messaging app.`;
    }
    return `${label} saved.`;
}

export default function MySelection() {
    const { member } = useMember();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [variant, setVariant] = useState("designed"); // "designed" | "sketch"
    const [activeDesignId, setActiveDesignId] = useState(null);
    const [favoriteId, setFavoriteId] = useState(null);
    const [savingFavorite, setSavingFavorite] = useState(false);
    // Share nudge — only shown right after a fresh selection (via router state).
    // Derived from location state so the initial render is correct; only the
    // slide-in transition + dismissal need real React state.
    const justSelected = location.state?.justSelected === true;
    const [shareNudgeVisible, setShareNudgeVisible] = useState(false);
    const [shareNudgeDismissed, setShareNudgeDismissed] = useState(false);
    const showShareNudge = justSelected && !shareNudgeDismissed;

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.get("/selections/me");
                setData(res.data);
                // Start on the member's favourite design (fallback: newest).
                const ds = res.data?.angel?.designs || [];
                const fav = res.data?.selection?.favoriteDesignId || null;
                setFavoriteId(fav);
                setActiveDesignId(
                    (fav && ds.some((d) => d.id === fav)) ? fav : (ds.length ? ds[ds.length - 1].id : null)
                );
                // Sketch-only angel: the sketch is the only look.
                if (!ds.length && res.data?.angel?.hasArtwork
                    && res.data.angel.mainImageUrl === res.data.angel.placeholderImageUrl) {
                    setVariant("sketch");
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Slide-in share nudge — fires only when the user just confirmed a pick.
    // We let the AngelSparks celebration play first (≈1.8s on detail page),
    // then a brief settle moment here before the nudge eases in.
    useEffect(() => {
        if (!justSelected) return;
        // Clear router state so a back/forward or refresh doesn't re-trigger.
        window.history.replaceState({}, "");
        const t = window.setTimeout(() => setShareNudgeVisible(true), 600);
        return () => window.clearTimeout(t);
    }, [justSelected]);

    const dismissShareNudge = () => {
        setShareNudgeVisible(false);
        // Let the fade-out finish before unmount.
        window.setTimeout(() => setShareNudgeDismissed(true), 400);
    };

    const handleDownload = async () => {
        if (!data?.angel || !data?.selection) return;
        setDownloading(true);
        try {
            const result = await downloadCertificate({
                angel: exportAngel,
                selectionDate: data.selection.selectedAt,
                memberName: (member && member.fullName) || (member && member.email) || "Squad Member",
                variant,
                firstMember: Boolean(data.selection.firstMember),
            });
            toast.success(toastFor("Certificate", result));
        } catch (e) {
            toast.error("Could not build certificate.");
            console.error(e);
        } finally {
            setDownloading(false);
        }
    };

    const handleShare = async () => {
        if (!data?.angel) return;
        setSharing(true);
        // If the nudge was open, dismiss it now that the user accepted.
        if (showShareNudge) dismissShareNudge();
        try {
            const result = await shareAngelCard({
                angel: exportAngel,
                memberName: (member && member.fullName) || (member && member.email) || "Squad Member",
                variant,
            });
            toast.success(toastFor("Share card", result));
        } catch (e) {
            toast.error("Could not build the share card.");
            console.error(e);
        } finally {
            setSharing(false);
        }
    };

    const handlePrint = async () => {
        if (!data?.angel) return;
        setPrinting(true);
        try {
            const result = await downloadFramedPrint({ angel: exportAngel, variant });
            toast.success(toastFor("Framed print", result));
        } catch (e) {
            toast.error("Could not build the framed print.");
            console.error(e);
        } finally {
            setPrinting(false);
        }
    };

    const nextChangeAllowedAt = data?.nextChangeAllowedAt;
    const cooldownDateLabel = useMemo(() => formatCooldownDate(nextChangeAllowedAt), [nextChangeAllowedAt]);

    if (loading) {
        return <PortalLayout><div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="selection-loading">…</div></PortalLayout>;
    }

    const angel = data?.angel;
    const selection = data?.selection;
    const cooldownActive = isCooldownActive(nextChangeAllowedAt);

    const hasSketch = angel?.hasArtwork && angel?.placeholderImageUrl;
    // Design gallery; sketch-only angels (no designs yet) get an empty list —
    // their sketch is the single available look.
    const designs = angel?.designs?.length
        ? angel.designs
        : (angel && angel.hasArtwork && angel.mainImageUrl && angel.mainImageUrl !== angel.placeholderImageUrl)
            ? [{ id: "legacy", url: angel.mainImageUrl, version: angel.imageVersion, label: "Design I" }]
            : [];
    const activeDesign = designs.find((d) => d.id === activeDesignId) || designs[designs.length - 1] || null;
    const displayedImageUrl = (hasSketch && variant === "sketch")
        ? angel.placeholderImageUrl
        : activeDesign?.url || angel?.mainImageUrl;
    const displayedImageVersion = (hasSketch && variant === "sketch")
        ? angel?.placeholderImageVersion
        : activeDesign?.version ?? angel?.imageVersion;
    // Downloads/shares always draw the member's active design when in
    // "designed" mode — swap it into the angel object the canvas libs read.
    const exportAngel = (angel && activeDesign)
        ? { ...angel, mainImageUrl: activeDesign.url, imageVersion: activeDesign.version }
        : angel;

    const chooseDesign = async (design) => {
        setVariant("designed");
        setActiveDesignId(design.id);
        // Persist as favourite (drives certificate/prints/cards) — only when
        // the angel actually has a gallery and this is a change.
        if (design.id === "legacy" || design.id === favoriteId || designs.length < 2) return;
        setSavingFavorite(true);
        try {
            await api.put("/selections/favorite-design", { designId: design.id });
            setFavoriteId(design.id);
            toast.success(`${design.label} is now your favourite — it will appear on your certificate, prints and share cards.`);
        } catch {
            toast.error("Could not save your favourite design.");
        } finally {
            setSavingFavorite(false);
        }
    };

    if (!selection || !angel) {
        return (
            <PortalLayout>
                <section className="max-w-3xl mx-auto px-6 md:px-12 pt-24 pb-32 text-center" data-testid="my-selection-empty">
                    <span className="font-label text-[11px] text-[#cfa052]">My selection</span>
                    <h1 className="mt-6 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]">
                        You haven&apos;t chosen yet.
                    </h1>
                    <p className="mt-6 font-poetic text-lg text-[#9a958a]">
                        Step into the grid. Take your time. The Angel you&apos;re meant for will quiet you.
                    </p>
                    <Link
                        to="/angels"
                        data-testid="selection-empty-cta"
                        className="inline-block mt-10 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300"
                    >
                        Browse the Angels
                    </Link>
                </section>
            </PortalLayout>
        );
    }

    return (
        <PortalLayout>
            <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-12 pb-32" data-testid="my-selection-page">
                <div className="text-center mb-14">
                    <span className="font-label text-[11px] text-[#cfa052]">Your Angel</span>
                    <h1 className="mt-4 font-serif-display text-5xl md:text-7xl tracking-tighter text-[#e8e4db]">
                        Sealed in <span className="italic">light</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a]">
                        Confirmed {new Date(selection.selectedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                    <a
                        href="https://dalboocreate.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                            // Fire-and-forget outbound-click attribution.
                            api.post("/track/outbound", { dest: "dalboocreate" }).catch(() => {});
                        }}
                        data-testid="selection-hero-merch-cta"
                        className="mt-7 inline-flex items-center gap-2.5 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-6 py-3 rounded-full font-label text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 shadow-[0_0_30px_rgba(207,160,82,0.25)]"
                    >
                        <ShoppingBag className="w-3.5 h-3.5" aria-hidden="true" />
                        Shop Angelwear at dalboocreate.com
                        <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </a>
                </div>

                {showShareNudge && (
                    <div
                        role="region"
                        aria-label="Share suggestion"
                        data-testid="selection-share-nudge"
                        className={`max-w-2xl mx-auto -mt-6 mb-10 transition-all duration-500 ease-out ${
                            shareNudgeVisible
                                ? "opacity-100 translate-y-0"
                                : "opacity-0 -translate-y-2"
                        }`}
                    >
                        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-gradient-to-r from-[#1a1714] to-[#151311] border border-[#cfa052]/30 rounded-sm px-5 py-4 shadow-[0_0_30px_rgba(207,160,82,0.08)]">
                            <Sparkles className="w-5 h-5 text-[#cfa052] shrink-0 mt-1 sm:mt-0" aria-hidden="true" />
                            <div className="flex-1 min-w-0">
                                <p className="font-poetic text-[#e8e4db] text-base leading-snug">
                                    Carry the moment with you — share your Angel with the squad.
                                </p>
                                <p className="mt-1 font-poetic text-[12px] text-[#6e6a62]">
                                    A keepsake card, ready for any post.
                                </p>
                            </div>
                            <div className="flex items-center gap-2 self-stretch sm:self-auto">
                                <button
                                    type="button"
                                    onClick={handleShare}
                                    disabled={sharing}
                                    data-testid="selection-share-nudge-cta"
                                    className="inline-flex items-center gap-2 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-5 py-2.5 font-label text-[11px] tracking-widest uppercase rounded-sm transition-colors duration-300 disabled:opacity-60"
                                >
                                    {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                                    Share
                                </button>
                                <button
                                    type="button"
                                    onClick={dismissShareNudge}
                                    data-testid="selection-share-nudge-dismiss"
                                    aria-label="Dismiss share suggestion"
                                    className="p-2 text-[#6e6a62] hover:text-[#e8e4db] transition-colors rounded-sm"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    <div className="flex flex-col gap-4">
                        <button
                            type="button"
                            onClick={() => setLightboxOpen(true)}
                            data-testid="selection-open-lightbox"
                            className="group relative aspect-[5/4] overflow-hidden bg-[#151311] border border-[#cfa052]/40 shadow-[0_0_40px_rgba(207,160,82,0.15)] ring-1 ring-[#cfa052]/40 text-left"
                        >
                            <img
                                src={withImageVersion(displayedImageUrl, displayedImageVersion)}
                                alt={`Angel ${angel.number} — ${variant}`}
                                className={`w-full h-full ${angel.hasArtwork ? "object-contain p-4" : "object-cover"}`}
                            />
                            <div className="absolute top-3 right-3 bg-[#cfa052] text-[#0c0b0a] text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm">
                                Chosen
                            </div>
                            <div className="absolute bottom-3 right-3 w-10 h-10 flex items-center justify-center bg-[#0c0b0a]/60 backdrop-blur-sm border border-white/10 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                <Maximize2 className="w-4 h-4 text-[#cfa052]" />
                            </div>
                        </button>

                        {(hasSketch || designs.length > 1) && (
                            <div
                                className="flex flex-wrap gap-2 self-start"
                                role="tablist"
                                aria-label="Choose artwork version"
                                data-testid="selection-variant-toggle"
                            >
                                {designs.map((d, idx) => {
                                    const isActive = variant === "designed" && activeDesign?.id === d.id;
                                    const isFavorite = designs.length > 1 && (favoriteId ? favoriteId === d.id : idx === designs.length - 1);
                                    return (
                                        <button
                                            key={d.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive}
                                            disabled={savingFavorite}
                                            onClick={() => chooseDesign(d)}
                                            data-testid={`selection-variant-design-${idx + 1}`}
                                            className={`inline-flex items-center gap-1.5 font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                                                isActive
                                                    ? "bg-[#cfa052] border-[#cfa052] text-[#0c0b0a]"
                                                    : "border-white/10 bg-[#151311] text-[#9a958a] hover:text-[#e8e4db]"
                                            }`}
                                        >
                                            {isFavorite && <Star className={`w-3 h-3 ${isActive ? "fill-[#0c0b0a]" : "fill-[#cfa052] text-[#cfa052]"}`} aria-label="Favourite design" />}
                                            {d.label}
                                        </button>
                                    );
                                })}
                                {hasSketch && (
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={variant === "sketch"}
                                        onClick={() => setVariant("sketch")}
                                        data-testid="selection-variant-sketch"
                                        className={`font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                                            variant === "sketch"
                                                ? "bg-[#cfa052] border-[#cfa052] text-[#0c0b0a]"
                                                : "border-white/10 bg-[#151311] text-[#9a958a] hover:text-[#e8e4db]"
                                        }`}
                                    >
                                        Original sketch
                                    </button>
                                )}
                            </div>
                        )}
                        {hasSketch && (
                            <p className="font-poetic text-xs text-[#6e6a62] max-w-md" data-testid="selection-variant-caption">
                                {variant === "sketch"
                                    ? "Downloads and shares below will use the original sketch."
                                    : designs.length > 1
                                        ? `Downloads and shares below will use ${activeDesign?.label || "this design"} — your favourite is marked with a star.`
                                        : "Downloads and shares below will use the redesigned, colour-printed version."}
                            </p>
                        )}
                    </div>

                    <div>
                        <span className="font-label text-[11px] text-[#cfa052]" data-testid="selection-angel-number">№ {angel.number}</span>
                        <h2 className="mt-4 font-serif-display text-6xl md:text-7xl tracking-tighter text-[#e8e4db]">
                            {angel.number}
                        </h2>
                        <p className="mt-8 font-poetic text-xl text-[#9a958a] leading-relaxed" data-testid="selection-status-line">
                            {angel.approvedAttributes?.length
                                ? `${angel.approvedAttributes.join(" · ")}.`
                                : (angel.openedForSelectionAt && Date.now() - new Date(angel.openedForSelectionAt).getTime() < 14 * 24 * 3600 * 1000)
                                    ? "Newly arrived — be the first to suggest their attributes."
                                    : `${angel.statusText}.`}
                        </p>
                        <p className="mt-6 font-poetic text-sm text-[#6e6a62] max-w-md">
                            This number and this silhouette are now linked to your name in the squad. If you ever need to make a change, our contact line is open.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
                            <button
                                type="button"
                                onClick={handleDownload}
                                disabled={downloading}
                                data-testid="selection-download-certificate"
                                className="inline-flex items-center gap-2 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300 disabled:opacity-60"
                            >
                                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                Download certificate
                            </button>

                            <button
                                type="button"
                                onClick={handlePrint}
                                disabled={printing}
                                data-testid="selection-download-print"
                                className="inline-flex items-center gap-2 border border-[#cfa052]/50 hover:border-[#cfa052] text-[#cfa052] hover:text-[#e0b569] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300 disabled:opacity-60"
                            >
                                {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Frame className="w-4 h-4" />}
                                Download framed print
                            </button>

                            <button
                                type="button"
                                onClick={handleShare}
                                disabled={sharing}
                                data-testid="selection-share-card"
                                className="inline-flex items-center gap-2 border border-[#cfa052]/50 hover:border-[#cfa052] text-[#cfa052] hover:text-[#e0b569] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300 disabled:opacity-60"
                            >
                                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                                Share my Angel
                            </button>
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 text-sm">
                            <Link
                                to="/angels"
                                data-testid="selection-back-to-grid"
                                className="font-label text-[10px] text-[#9a958a] hover:text-[#cfa052] transition-colors"
                            >
                                ← Back to grid
                            </Link>
                            {cooldownActive ? (
                                <span
                                    data-testid="selection-cooldown-hint"
                                    className="font-label text-[10px] text-[#6e6a62]"
                                >
                                    Next change available {cooldownDateLabel}
                                </span>
                            ) : (
                                <Link
                                    to="/angels"
                                    data-testid="selection-change-link"
                                    className="font-label text-[10px] text-[#9a958a] hover:text-[#cfa052] transition-colors"
                                >
                                    Change my Angel →
                                </Link>
                            )}
                        </div>

                        {/* Soft merch invitation — non-pushy, sits below the actions
                            with breathing room and a hairline divider so it reads
                            as an offering rather than a sales pitch. */}
                        <div
                            className="mt-14 pt-8 border-t border-white/5 max-w-md"
                            data-testid="selection-merch-cta"
                        >
                            <span className="font-label text-[10px] tracking-[0.3em] text-[#cfa052] uppercase">
                                Carry your Angel further
                            </span>
                            <p className="mt-3 font-poetic text-[15px] text-[#9a958a] leading-relaxed">
                                Explore a deeper connection with your chosen Angel at{" "}
                                <a
                                    href="https://dalboocreate.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                        api.post("/track/outbound", { dest: "dalboocreate" }).catch(() => {});
                                    }}
                                    className="text-[#cfa052] hover:text-[#e8e4db] underline underline-offset-4 decoration-[#cfa052]/40 transition-colors"
                                    data-testid="selection-merch-link"
                                >
                                    dalboocreate.com
                                </a>{" "}
                                — where you can find merchandise designed specifically for the Angel Squad.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            {lightboxOpen && (
                <Lightbox
                    angel={angel}
                    imageUrl={displayedImageUrl}
                    imageVersion={displayedImageVersion}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </PortalLayout>
    );
}
